import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { AuthBrandPanel } from '../components/AuthBrandPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Alert, AlertDescription } from '../components/ui/alert';
import { config } from '../config';
import { endSession } from '../utils/authSession';
import { resolvePostAuthPath } from '../utils/postAuthPath';

const loginSchema = z.object({
  email: z.string().min(1, 'Enter your work email.').email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password.'),
});

const passwordSetupSchema = z.object({
  password: z.string().min(8, 'Use at least 8 characters.'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "These passwords don't match.",
  path: ["confirmPassword"],
});

type LoginFormData = z.infer<typeof loginSchema>;
type PasswordSetupFormData = z.infer<typeof passwordSetupSchema>;

const LINK_INVALID = "This link has expired or isn't valid. Links work for 24 hours. Ask your admin to send you a new one.";
const LINK_UNCHECKED = "We couldn't check your link. Open it again from the email to try again.";
const PASSWORD_NOT_SAVED = "Your password wasn't saved. Try again.";

interface OrganizationBranding {
  displayName: string;
  logoUrl: string | null;
  organizationCode: string;
}

const UniversalAuthPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [orgBranding, setOrgBranding] = useState<OrganizationBranding>({
    displayName: 'Good Outcomes',
    logoUrl: null,
    organizationCode: ''
  });
  const [isVerifyingToken, setIsVerifyingToken] = useState(false);
  const { login } = useAuth();

  const getPostAuthPath = (): string =>
    resolvePostAuthPath(searchParams.get('return_to'), window.location.origin);

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const passwordSetupForm = useForm<PasswordSetupFormData>({
    resolver: zodResolver(passwordSetupSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  // Back to the sign-in card with the link dropped from state and the URL.
  const returnToSignIn = useCallback((message: string) => {
    setToken(null);
    setUserEmail('');
    setOrgBranding({ displayName: 'Good Outcomes', logoUrl: null, organizationCode: '' });
    setError(message);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('token');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const verifyToken = useCallback(async (tokenValue: string) => {
    setIsVerifyingToken(true);
    setError('');
    try {
      const response = await fetch(`${config.API_URL}/v1/auth/magic-link/verify?token=${encodeURIComponent(tokenValue)}`);
      if (response.ok) {
        const data = await response.json();
        setUserEmail(data.email);

        // Set organization branding from API response
        setOrgBranding({
          displayName: data.branding?.display_name || data.organization_name,
          logoUrl: data.branding?.logo_url,
          organizationCode: data.organization_code
        });
      } else {
        // A 4xx is the API refusing the token; anything else is a check that failed.
        returnToSignIn(response.status < 500 ? LINK_INVALID : LINK_UNCHECKED);
      }
    } catch (err) {
      returnToSignIn(LINK_UNCHECKED);
    } finally {
      setIsVerifyingToken(false);
    }
  }, [returnToSignIn]);

  // Check for token in URL on mount
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      void verifyToken(tokenParam);
    }
  }, [searchParams, verifyToken]);


  const handleLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');

    try {
      const success = await login(data.email, data.password);

      if (success) {
        window.location.href = getPostAuthPath();
      } else {
        setError('Incorrect email or password.');
      }
    } catch (err) {
      setError("We couldn't sign you in just now. Try again in a moment.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async (data: PasswordSetupFormData) => {
    if (!token || !orgBranding.organizationCode) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.API_URL}/v1/auth/magic-link/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          password: data.password,
        }),
      });

      if (response.status === 400 || response.status === 401) {
        // The token was refused: the link is no longer usable.
        returnToSignIn(LINK_INVALID);
        return;
      }
      if (!response.ok) {
        throw new Error(`Set password failed with ${response.status}`);
      }

      const tokenData = await response.json();

      // Store auth data
      const userData = {
        user_id: tokenData.user_id,
        username: tokenData.username,
        email: userEmail,
        organization_id: tokenData.organization_id,
        organization_code: tokenData.organization_code,
        role: tokenData.role,
      };

      // A magic link is a session start that never goes through login(), so it
      // sweeps for the same reason: the previous user's state must not survive.
      endSession();

      localStorage.setItem('token', tokenData.access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');

      window.location.href = getPostAuthPath();
    } catch (err) {
      setError(PASSWORD_NOT_SAVED);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const errorAlert = error && (
    <Alert variant="destructive" className="mb-4">
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  );

  return (
    <div className="flex min-h-screen">
      <AuthBrandPanel />

      <div className="flex flex-1 items-center justify-center bg-white px-4 py-12">
        {isVerifyingToken ? (
          <div className="w-full max-w-[400px] p-8">
            <p role="status" className="text-sm text-muted-foreground">Checking your link…</p>
          </div>
        ) : token && userEmail && orgBranding.organizationCode ? (
          <Form key="set-password" {...passwordSetupForm}>
            <form onSubmit={passwordSetupForm.handleSubmit(handleSetPassword)} className="w-full max-w-[400px] p-8" noValidate>
              <h1 className="text-[28px] font-extrabold leading-tight tracking-[-0.02em]">Set your password</h1>
              <p className="mt-1.5 mb-6 text-sm text-muted-foreground">{orgBranding.displayName} on Good Outcomes</p>

              {errorAlert}

              <div className="grid gap-2">
                <Label htmlFor="setup-email">Work email</Label>
                <Input id="setup-email" type="email" autoComplete="username" value={userEmail} readOnly className="bg-muted" />
                <p className="text-sm text-muted-foreground">
                  Not you?{' '}
                  <button type="button" onClick={() => returnToSignIn('')} className="text-primary hover:underline">
                    Sign in instead
                  </button>
                </p>
              </div>
              <FormField
                control={passwordSetupForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="mt-4">
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormDescription>At least 8 characters.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={passwordSetupForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="mt-4">
                    <FormLabel>Confirm password</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" size="lg" className="mt-5 w-full" disabled={isLoading}>
                Set password and sign in
              </Button>
            </form>
          </Form>
        ) : (
          <Form key="sign-in" {...loginForm}>
            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="w-full max-w-[400px] p-8" noValidate>
              <h1 className="text-[28px] font-extrabold leading-tight tracking-[-0.02em]">Sign in</h1>
              <p className="mt-1.5 mb-6 text-sm text-muted-foreground">Use your work email.</p>

              {errorAlert}

              <FormField
                control={loginForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Work email</FormLabel>
                    <FormControl>
                      <Input type="email" autoComplete="email" placeholder="name@company.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={loginForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="mt-4">
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="current-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" size="lg" className="mt-5 w-full" disabled={isLoading}>
                Sign in
              </Button>

              <p className="mt-4 text-center text-[12.5px] text-muted-foreground">
                Forgot your password? Ask your admin to send you a new link.
              </p>
              <p className="mt-1.5 text-center text-[12.5px] text-muted-foreground">
                Access is by invitation only.
              </p>
            </form>
          </Form>
        )}
      </div>
    </div>
  );
};

export default UniversalAuthPage;
